/**
 * WalkthroughPersonaHeader — persona avatar + scenario name + dateline.
 *
 * The "who + what + when" header that identifies a walkthrough run. Used
 * at the top of hero cards and the walkthrough detail view. Scenario
 * name is the headline (h3, sans semibold); persona and timestamp
 * collapse into a single muted line underneath.
 */

import { Link } from "react-router-dom";
import { formatRelativeTime } from "@/lib/freshness";
import { PersonaAvatar } from "@/components/persona-identity";
import { Kicker } from "@/components/ui/kicker";

interface WalkthroughPersonaHeaderProps {
  personaName?: string | null;
  scenarioName?: string | null;
  /** Link target for persona avatar and name */
  personaTo?: string | null;
  /**
   * Link target for the scenario headline. Pass `null` when a caller
   * resolves its ternary at call site (e.g. `scenarioId ? url : null`).
   * If `scenarioName` is falsy the link is suppressed regardless — a
   * "Walkthrough" placeholder headline never links anywhere.
   */
  scenarioTo?: string | null;
  /** Timestamp — completedAt preferred, falls back to startedAt/createdAt */
  timestamp: string | Date | null;
  /** Persona avatar size (default md) */
  avatarSize?: "sm" | "md" | "lg";
}

export function WalkthroughPersonaHeader({
  personaName,
  personaTo,
  scenarioName,
  scenarioTo,
  timestamp,
  avatarSize = "md",
}: WalkthroughPersonaHeaderProps) {
  const personaFirstName = personaName?.split(" ")[0];
  const headline = scenarioName ?? "Walkthrough";
  const linkTo = scenarioName && scenarioTo ? scenarioTo : null;
  const timeIso =
    timestamp instanceof Date
      ? timestamp.toISOString()
      : typeof timestamp === "string"
        ? timestamp
        : null;

  const metaParts: React.ReactNode[] = [];
  if (personaFirstName) {
    metaParts.push(
      personaTo ? (
        <Link
          key="persona"
          to={personaTo}
          className="transition-colors hover:text-foreground hover:underline"
        >
          As experienced by {personaFirstName}
        </Link>
      ) : (
        `As experienced by ${personaFirstName}`
      ),
    );
  }
  if (timeIso) {
    metaParts.push(
      <time key="t" dateTime={timeIso}>
        {formatRelativeTime(timeIso)}
      </time>,
    );
  }

  const avatar = personaName ? (
    <PersonaAvatar name={personaName} size={avatarSize} />
  ) : null;

  return (
    <div className="flex items-start gap-4">
      {avatar &&
        (personaTo ? (
          <Link
            to={personaTo}
            className="transition-opacity hover:opacity-85"
            aria-label={`View persona profile for ${personaName}`}
          >
            {avatar}
          </Link>
        ) : (
          avatar
        ))}
      <div className="min-w-0 flex-1 space-y-0.5">
        <h3 className="text-md font-semibold text-foreground">
          {linkTo ? (
            <Link to={linkTo} className="underline-offset-4 hover:underline">
              {headline}
            </Link>
          ) : (
            headline
          )}
        </h3>
        {metaParts.length > 0 && (
          <Kicker className="tabular-nums">
            {metaParts.map((part, i) => (
              <span key={i}>
                {i > 0 && " · "}
                {part}
              </span>
            ))}
          </Kicker>
        )}
      </div>
    </div>
  );
}
